// Лента сообщений (SPEC §4.2). Строит «строки ленты» (buildLedger: DateSeparator/UnreadDivider/
// MessageBubble с grouping/deliveryState/reply/forward/reactions), доскролл к сообщению (deep-link/
// reply-jump) + подсветка, пагинация вверх (loadOlder с сохранением позиции), markRead по достижению
// низа. Маппинг доменных Message → props MessageBubble — чистый (chat/derive.ts), здесь — рендер/скролл.
//
// ВИРТУАЛИЗАЦИЯ (NFR-08): окно рендера и измерение высот строк — браузерная механика (ResizeObserver/
// измеряемые высоты), подтверждается browser-verify (SPEC §6). Здесь рендерится корректная полная
// лента со стабильными ключами (derive.messageKey) и якорями скролла; окно-виртуализация — слой
// оптимизации поверх, который ловит integration-gate (jsdom высоты не меряет — это и есть «typecheck/
// unit ≠ виртуализация работает», feedback_typecheck_not_feature).

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from 'react'
import type { ChatId, Message, MessageId, UserId } from '@tele-chat/shared-contracts'
import type { TimelineEntry, ClientStore } from '@tele-chat/client-store'
import {
  MessageBubble,
  DateSeparator,
  UnreadDivider,
  Spinner,
  type ContextMenuAnchor,
} from '@tele-chat/design-system'
import {
  buildLedger,
  directionOf,
  deliveryStateOf,
  isOptimistic,
} from '../chat/derive.js'
import { originLabelOf, senderDisplayName } from '../chat/origin.js'
import { useVoicePlayback } from '../chat/useVoicePlayback.js'
import { useMediaTransfers } from '../media/MediaTransfersContext.js'
import { useNotifications } from '../notifications/NotificationsContext.js'
import { useStore, useSelector } from '../store/StoreContext.js'

export interface MessageListProps {
  chatId: ChatId
  selfId: UserId
  timeline: readonly TimelineEntry[]
  lastReadSeq: number
  isGroup: boolean
  resolveName(userId: UserId): string
  /** Сообщение для доскролла (?msg=<messageId>, канон L) — со сменой меняется цель. */
  scrollToMessageId?: MessageId
  onReply(message: Message): void
  onReplyQuoteClick(messageId: MessageId): void
  onOpenContextMenu(message: Message, anchor: ContextMenuAnchor): void
  onReact(messageId: MessageId, emoji: string): void
  onOpenMedia(message: Message, attachmentIndex: number): void
  onMarkRead(seq: number): void
}

export function MessageList(props: MessageListProps): JSX.Element {
  const {
    chatId,
    selfId,
    timeline,
    lastReadSeq,
    isGroup,
    resolveName,
    scrollToMessageId,
    onReply,
    onReplyQuoteClick,
    onOpenContextMenu,
    onReact,
    onOpenMedia,
    onMarkRead,
  } = props
  const store = useStore()
  const { notify } = useNotifications()
  // Воспроизведение голосовых: один аудио-элемент на ленту, ошибки — тостом.
  const voicePlayback = useVoicePlayback((msg) => notify({ kind: 'error', title: msg }))
  // Скачивание вложений с прогрессом (DLP-6): кнопка «Скачать» на плашке, кольцо/полоса, отмена.
  const transfers = useMediaTransfers()
  const scrollRef = useRef<HTMLDivElement>(null)
  const rowRefs = useRef<Map<string, HTMLDivElement>>(new Map())
  const loadingOlder = useRef(false)
  const prevScrollHeight = useRef<number>(0)
  const highlightedId = useRef<MessageId | null>(null)
  // Реактивный флаг ТОЛЬКО для индикатора: спиннер истории виден лишь во время реального фетча
  // loadOlder, а не всегда при непустой ленте (иначе любой чат выглядел «вечно загружающимся»).
  const [fetchingOlder, setFetchingOlder] = useState(false)

  const ledger = buildLedger(timeline, lastReadSeq)

  const receiptsFor = useCallback(
    (seq: number) => store.select.receiptsFor(chatId, seq),
    [store, chatId],
  )

  // Живая реактивность тиков доставки/прочтения. receipt.delivered/read меняют только state.receipts
  // (не timeline/chat), поэтому подписанные селекторы ConversationScreen не перерисовывают ленту — без
  // этой подписки тик «прочитано» не появлялся бы в реальном времени. Подписываемся на «отпечаток»
  // receipts своих сообщений: при продвижении read/delivered строка меняется → лента перерисовывается.
  const receiptsSelector = useCallback(
    (s: ClientStore): string =>
      timeline
        .filter((e) => !isOptimistic(e) && (e as Message).senderId === selfId)
        .map((e) => {
          const seq = (e as Message).seq
          const r = s.select.receiptsFor(chatId, seq)
          return `${seq}:${r.read.length}:${r.delivered.length}`
        })
        .join('|'),
    [timeline, selfId, chatId],
  )
  useSelector(receiptsSelector)

  // Доскролл вниз при появлении новых сообщений, если пользователь был у низа.
  const atBottomRef = useRef(true)

  // Вход в чат: инстанс ленты ПЕРЕИСПОЛЬЗУЕТСЯ между чатами — без сброса atBottom/якоря прошлого
  // чата протекали в новый, и лента открывалась «где стояла». Telegram-поведение: есть непрочитанные
  // → разделитель «Непрочитанные» у верха вьюпорта; нет → мгновенно к последним сообщениям.
  const initialScrollPending = useRef(true)
  useLayoutEffect(() => {
    initialScrollPending.current = true
    atBottomRef.current = true
    loadingOlder.current = false
  }, [chatId])

  useLayoutEffect(() => {
    const el = scrollRef.current
    if (el === null) return
    if (initialScrollPending.current) {
      if (timeline.length === 0) return // данные чата ещё едут (hydrate/backfill) — позиционируем по первым
      initialScrollPending.current = false
      const unreadKey = ledger.find((r) => r.type === 'unread')?.key
      const unreadNode = unreadKey !== undefined ? rowRefs.current.get(unreadKey) : undefined
      if (unreadNode !== undefined && typeof unreadNode.scrollIntoView === 'function') {
        unreadNode.scrollIntoView({ block: 'start' })
        // Мало непрочитанных → низ уже виден: живые сообщения продолжают прижимать низ.
        atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80
      } else {
        el.scrollTop = el.scrollHeight
        atBottomRef.current = true
      }
      return
    }
    if (loadingOlder.current) {
      // Сохраняем позицию при подгрузке истории сверху (anchor).
      const delta = el.scrollHeight - prevScrollHeight.current
      el.scrollTop += delta
      loadingOlder.current = false
      return
    }
    if (atBottomRef.current) el.scrollTop = el.scrollHeight
  }, [timeline])

  // Клавиатура/resize: вьюпорт ленты сжимается снизу (visualViewport ужимает #root), scrollTop
  // при этом не меняется → свежие сообщения «уезжают под клавиатуру», видна старая часть истории
  // (видео-раскадровка iPhone 03.07). Telegram-поведение: был у низа — низ остаётся прижатым.
  // Читавшему старую историю (atBottom=false) позицию не дёргаем.
  useEffect(() => {
    const el = scrollRef.current
    if (el === null || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => {
      if (atBottomRef.current) el.scrollTop = el.scrollHeight
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // Доскролл к конкретному сообщению (deep-link/reply-jump) + подсветка.
  useEffect(() => {
    if (scrollToMessageId === undefined) return
    const key = `msg-${scrollToMessageId}`
    const node = rowRefs.current.get(key)
    if (node !== undefined) {
      node.scrollIntoView({ block: 'center' })
      highlightedId.current = scrollToMessageId
      node.setAttribute('data-highlight', '')
      const t = setTimeout(() => node.removeAttribute('data-highlight'), 1600)
      return () => clearTimeout(t)
    }
    // Сообщение не в окне — догружаем историю (до появления нужного seq).
    void store.actions.loadOlder(chatId)
    return undefined
  }, [scrollToMessageId, chatId, store, timeline])

  // Отметка прочтения (FR-DELIVERY-04): единый вход с монотонным дедупом (store шлёт WS-кадр
  // `read` на каждый вызов — без guard'а скролл у низа спамил бы кадрами каждый пиксель).
  const lastMarkedSeq = useRef(0)
  useEffect(() => {
    lastMarkedSeq.current = 0 // смена чата — новый отсчёт (инстанс MessageList переиспользуется)
  }, [chatId])
  const markReadIfAtBottom = useCallback((): void => {
    const el = scrollRef.current
    if (el === null || timeline.length === 0) return
    if (el.scrollHeight - el.scrollTop - el.clientHeight >= 80) return
    const last = timeline[timeline.length - 1]
    if (last === undefined || isOptimistic(last)) return
    const seq = (last as Message).seq
    if (seq > lastMarkedSeq.current) {
      lastMarkedSeq.current = seq
      onMarkRead(seq)
    }
  }, [timeline, onMarkRead])

  // Низ виден БЕЗ скролла: короткая история не переполняет вьюпорт (scroll-событие не случается
  // НИКОГДА) и live-сообщение при уже видимом низе. Раньше прочтение вешалось только на onScroll —
  // в коротких чатах счётчик непрочитанных не сбрасывался до ручного шевеления ленты.
  // Порядок: layout-эффект автоскролла выше уже спозиционировал ленту к низу.
  useEffect(() => {
    markReadIfAtBottom()
  }, [markReadIfAtBottom])

  const onScroll = useCallback((): void => {
    const el = scrollRef.current
    if (el === null) return
    atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80
    // Пагинация вверх (FR-PWA-06): у верхней границы — loadOlder с сохранением anchor.
    if (el.scrollTop < 60 && !loadingOlder.current) {
      loadingOlder.current = true
      prevScrollHeight.current = el.scrollHeight
      setFetchingOlder(true)
      void store.actions.loadOlder(chatId).finally(() => setFetchingOlder(false))
    }
    // Отметка прочтения при достижении низа (FR-DELIVERY-04).
    markReadIfAtBottom()
  }, [store, chatId, markReadIfAtBottom])

  // overflowX hidden ОБЯЗАТЕЛЕН: overflow-y:auto делает вычисленный overflow-x тоже auto, и любой
  // переполнивший пузырь (длинное имя файла и т.п.) дарит ленте горизонтальную прокрутку (iPhone 03.07).
  const container: CSSProperties = { flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', display: 'flex', flexDirection: 'column' }

  const setRowRef = useCallback((key: string, node: HTMLDivElement | null): void => {
    if (node === null) rowRefs.current.delete(key)
    else rowRefs.current.set(key, node)
  }, [])

  return (
    <div ref={scrollRef} data-message-list="" style={container} onScroll={onScroll}>
      {/* индикатор подгрузки истории сверху — только во время реального фетча */}
      {fetchingOlder ? (
        <div data-load-older="" style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-2)' }}>
          <Spinner size={16} label="Загрузка истории" />
        </div>
      ) : null}

      {ledger.map((row) => {
        if (row.type === 'date') {
          return <DateSeparator key={row.key} date={row.date} />
        }
        if (row.type === 'unread') {
          // Обёртка с ref — цель позиционирования «вход в чат с непрочитанными» (initialScroll).
          return (
            <div key={row.key} ref={(node) => setRowRef(row.key, node)} data-row-key={row.key}>
              <UnreadDivider />
            </div>
          )
        }
        const entry = row.entry
        const direction = directionOf(entry, selfId)
        const deliveryState = deliveryStateOf(entry, selfId, receiptsFor)
        const committed = !isOptimistic(entry)
        const message = entry as Message
        // Голосовое: подставляем обмеренную длительность вместо серверного 0 (live-webm истории) —
        // иммутабельным клоном, доменный Message не мутируем.
        const isVoice = message.kind === 'voice' && message.voice !== undefined
        const displayMessage =
          isVoice && message.voice && message.voice.waveform.durationMs <= 0
            ? {
                ...message,
                voice: {
                  ...message.voice,
                  waveform: { ...message.voice.waveform, durationMs: voicePlayback.displayDurationMs(message) },
                },
              }
            : message
        return (
          <div key={row.key} ref={(node) => setRowRef(row.key, node)} data-row-key={row.key}>
            <MessageBubble
              message={displayMessage}
              voicePlayback={isVoice ? voicePlayback.forMessage(message) : undefined}
              direction={direction}
              grouping={row.grouping}
              deliveryState={deliveryState}
              senderName={isGroup && direction === 'in' ? senderDisplayName(message, resolveName) : undefined}
              senderColorKeyId={message.senderId}
              originLabel={originLabelOf(message)}
              resolveName={resolveName}
              onReply={committed ? () => onReply(message) : undefined}
              onReplyQuoteClick={onReplyQuoteClick}
              onReact={committed ? onReact : undefined}
              onOpenContextMenu={
                committed ? (id, anchor) => onOpenContextMenu(message, anchor) : undefined
              }
              onOpenMedia={(_id, idx) => onOpenMedia(message, idx)}
              onDownload={
                // Только закоммиченные: у оптимистичного вложения нет серверного id — /blob дал бы 404.
                committed
                  ? (_id, idx) => {
                      const att = message.attachments?.[idx]
                      if (att !== undefined) transfers.download(att)
                    }
                  : undefined
              }
              transferOf={committed ? transfers.transferOf : undefined}
              onCancelTransfer={(_id, idx) => {
                const att = message.attachments?.[idx]
                if (att !== undefined) transfers.cancel(att.id)
              }}
            />
          </div>
        )
      })}
    </div>
  )
}
